// A short animated fight shown in the result message: the two portraits trade blows,
// damage numbers pop, then the winner lights up and the crowd reacts.
import { useEffect, useState } from 'react';
import { Portrait } from '../art/Portrait.tsx';
import { play } from './sound.ts';

export interface Fighter { seed: number; playerId?: number; name: string; art?: React.ReactNode }

type Beat = { at: number; who: 'left' | 'right'; dmg?: number; sound: string };

export function FightClash({ left, right, won, dealt, taken, mode = 'fight', myHealth }: {
  left: Fighter;
  right: Fighter;
  won: boolean;
  dealt: number;
  taken: number;
  mode?: 'fight' | 'hit' | 'rob' | 'boss';
  /** Your health after the fight and your max health, to drain the bar. */
  myHealth?: { after: number; max: number };
}) {
  const [step, setStep] = useState(-1);
  const beats: Beat[] = mode === 'rob'
    ? [{ at: 0, who: 'right', sound: 'whoosh' }, { at: 500, who: won ? 'right' : 'left', dmg: won ? undefined : taken, sound: won ? 'coins' : 'punch' }]
    : [
        { at: 0, who: 'right', dmg: Math.ceil(dealt * 0.4), sound: mode === 'boss' ? 'bossHit' : 'punch' },
        { at: 320, who: 'left', dmg: Math.ceil(taken * 0.6), sound: 'gunshot' },
        { at: 640, who: 'right', dmg: dealt - Math.ceil(dealt * 0.4), sound: mode === 'boss' ? 'bossHit' : 'punch' },
        { at: 900, who: 'left', dmg: taken - Math.ceil(taken * 0.6), sound: 'punch' },
      ];
  const end = beats[beats.length - 1].at + 350;
  useEffect(() => {
    const timers = beats.map((b, i) => setTimeout(() => { setStep(i); play(b.sound); }, b.at));
    timers.push(setTimeout(() => {
      setStep(beats.length);
      if (mode === 'rob') play(won ? 'chaching' : 'groan');
      else play(won ? 'cheer' : 'groan');
    }, end));
    return () => timers.forEach(clearTimeout);
  }, []);
  const done = step >= beats.length;
  const cur = step >= 0 && step < beats.length ? beats[step] : null;
  const hp = myHealth ? {
    before: Math.min(myHealth.max, myHealth.after + taken),
    shown: Math.max(0, Math.min(myHealth.max, myHealth.after + taken - beats.slice(0, step + 1).filter((b) => b.who === 'left').reduce((s, b) => s + (b.dmg ?? 0), 0))),
  } : null;
  const side = (f: Fighter, who: 'left' | 'right') => {
    const hit = cur?.who === who;
    const winner = done && ((who === 'left') === won);
    const loser = done && !winner;
    return (
      <div className={`clash-side ${who} ${hit ? 'hit' : ''} ${winner ? 'winner' : ''} ${loser ? 'loser' : ''}`}>
        <div className="clash-portrait">
          {f.art ?? <Portrait seed={f.seed} playerId={f.playerId} size={64} />}
          {hit && cur?.dmg ? <span key={step} className="clash-dmg">-{cur.dmg}</span> : null}
          {hit && <span key={`f${step}`} className="clash-flash" />}
        </div>
        <div className="clash-name">{f.name}</div>
        {who === 'left' && hp && myHealth && (
          <div className="clash-hp"><div style={{ width: `${(hp.shown / myHealth.max) * 100}%` }} /></div>
        )}
      </div>
    );
  };
  return (
    <div className={`clash ${mode}`}>
      {side(left, 'left')}
      <div className={`clash-vs ${done ? (won ? 'won' : 'lost') : ''}`}>{done ? (won ? (mode === 'rob' ? 'GOT AWAY!' : 'WIN!') : (mode === 'rob' ? 'CAUGHT!' : 'LOSS')) : 'VS'}</div>
      {side(right, 'right')}
    </div>
  );
}
